import { DropPreviewType } from "@anaconda/core/drop/types"
import {
  extractOriginalMediaUrl,
  isRaribleThumbnailUrl,
} from "./rarible-media-url"

/**
 * Media type detection utility based on file extensions and MIME types
 */

interface MediaTypeMap {
  [key: string]: DropPreviewType
}

const FILE_EXTENSIONS: MediaTypeMap = {
  // Images
  png: DropPreviewType.Image,
  jpg: DropPreviewType.Image,
  jpeg: DropPreviewType.Image,
  gif: DropPreviewType.Image,
  svg: DropPreviewType.Image,
  webp: DropPreviewType.Image,
  avif: DropPreviewType.Image,

  // Videos
  mp4: DropPreviewType.Video,
  webm: DropPreviewType.Video,
  mov: DropPreviewType.Video,
  avi: DropPreviewType.Video,
  mkv: DropPreviewType.Video,

  // 3D Models
  glb: DropPreviewType.ThreeD,
  gltf: DropPreviewType.ThreeD,
  usd: DropPreviewType.ThreeD,
  usdz: DropPreviewType.ThreeD,
}

const MIME_TYPES: MediaTypeMap = {
  // Images
  "image/png": DropPreviewType.Image,
  "image/jpeg": DropPreviewType.Image,
  "image/gif": DropPreviewType.Image,
  "image/svg+xml": DropPreviewType.Image,
  "image/webp": DropPreviewType.Image,
  "image/avif": DropPreviewType.Image,

  // Videos
  "video/mp4": DropPreviewType.Video,
  "video/webm": DropPreviewType.Video,
  "video/quicktime": DropPreviewType.Video,
  "video/x-msvideo": DropPreviewType.Video,

  // 3D Models
  "model/gltf-binary": DropPreviewType.ThreeD,
  "model/gltf+json": DropPreviewType.ThreeD,
  "model/vnd.usd": DropPreviewType.ThreeD,
  "model/vnd.usdz+zip": DropPreviewType.ThreeD,
}

/**
 * Extract file extension from URL pathname, ignoring query parameters
 */
function getExtensionFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url)
    // Remove query params and hash
    const pathname = urlObj.pathname.toLowerCase()
    const lastDotIndex = pathname.lastIndexOf(".")
    const lastSlashIndex = pathname.lastIndexOf("/")

    // Make sure the dot comes after the last slash (not in a directory name)
    if (lastDotIndex > lastSlashIndex && lastDotIndex !== -1) {
      return pathname.substring(lastDotIndex + 1)
    }
    return null
  } catch {
    // Fallback for malformed URLs
    const parts = url.toLowerCase().split("?")[0].split("#")[0].split(".")
    if (parts.length > 1) {
      return parts[parts.length - 1]
    }
    return null
  }
}

/**
 * Detect media type from URL by examining file extension
 * Handles Rarible CDN thumbnail URLs by extracting the original URL first
 */
export function detectMediaTypeFromUrl(url?: string): DropPreviewType {
  if (!url) return DropPreviewType.Image

  let urlToCheck = url

  // If it's a Rarible thumbnail URL, extract the original URL first
  if (isRaribleThumbnailUrl(url)) {
    const originalUrl = extractOriginalMediaUrl(url)
    if (originalUrl) {
      urlToCheck = originalUrl
    }
  }

  // Check extension on the original/decoded URL first (most accurate)
  const extension = getExtensionFromUrl(urlToCheck)
  if (extension && FILE_EXTENSIONS[extension]) {
    return FILE_EXTENSIONS[extension]
  }

  // Fallback: Check Rarible CDN URL path for media type indicators
  // This handles cases where the original URL doesn't have an extension
  if (isRaribleThumbnailUrl(url)) {
    const lowerUrl = url.toLowerCase()
    if (lowerUrl.includes("/video/") || lowerUrl.includes("t_video_thumb")) {
      return DropPreviewType.Video
    }
    if (lowerUrl.includes("/image/") || lowerUrl.includes("t_image_thumb")) {
      return DropPreviewType.Image
    }
  }

  return DropPreviewType.Image
}

/**
 * Detect media type from MIME type
 */
export function detectMediaTypeFromMimeType(
  mimeType?: string,
): DropPreviewType {
  if (!mimeType) return DropPreviewType.Image

  const cleanMimeType = mimeType.toLowerCase().split(";")[0].trim()
  return MIME_TYPES[cleanMimeType] || DropPreviewType.Image
}

/**
 * Check if URL points to a 3D model
 */
export function is3DModel(url?: string): boolean {
  return detectMediaTypeFromUrl(url) === DropPreviewType.ThreeD
}

/**
 * Check if URL points to a video
 */
export function isVideo(url?: string): boolean {
  return detectMediaTypeFromUrl(url) === DropPreviewType.Video
}

/**
 * Check if URL points to an image
 */
export function isImage(url?: string): boolean {
  return detectMediaTypeFromUrl(url) === DropPreviewType.Image
}

/**
 * Get supported 3D model extensions
 */
export function getSupportedThreeDExtensions(): string[] {
  return Object.keys(FILE_EXTENSIONS).filter(
    (ext) => FILE_EXTENSIONS[ext] === DropPreviewType.ThreeD,
  )
}

/**
 * Get supported video extensions
 */
export function getSupportedVideoExtensions(): string[] {
  return Object.keys(FILE_EXTENSIONS).filter(
    (ext) => FILE_EXTENSIONS[ext] === DropPreviewType.Video,
  )
}
